import { useEffect, useMemo, useState } from 'react';
import { Popover, Tree, Input, Button, Spin, Empty, Badge, Typography } from 'antd';
import { DownOutlined, FilterOutlined } from '@ant-design/icons';
import { getUserHierarchyTree } from '../../services/dashboardApi';

const { Text } = Typography;

// Map the server's tree onto antd's {key,title,children} shape. The server has
// already scoped and nested it  , so there is no tree-building to do here.
function toTreeData(nodes) {
  return (nodes ?? []).map((node) => ({
    key: node.userId,
    title: node.isSelf ? `${node.name} (You)` : node.name,
    // Kept for search, which matches on name or email.
    searchText: `${node.name} ${node.email ?? ''}`.toLowerCase(),
    children: toTreeData(node.children),
  }));
}

// Keep a node when it matches, or when any descendant does — so a match deep in
// the tree stays reachable through its ancestors rather than being orphaned.
function filterTree(nodes, term) {
  const out = [];
  for (const node of nodes) {
    const children = filterTree(node.children ?? [], term);
    if (node.searchText.includes(term) || children.length > 0) {
      out.push({ ...node, children });
    }
  }
  return out;
}

function collectKeys(nodes, acc = []) {
  for (const node of nodes) {
    acc.push(node.key);
    collectKeys(node.children ?? [], acc);
  }
  return acc;
}

/**
 * A "Filter by" control: a checkbox tree of the account's users, nested by
 * reporting manager. Used by the dashboard to scope its cards/calendar to a
 * chosen set of users.
 *
 * Checking a manager selects their whole subtree. The backend expands and
 * re-checks that itself, so this is a convenience rather than the security
 * boundary — but it does mean what the user sees ticked is what gets counted.
 */
export default function HierarchyUserFilter({ value = [], onChange }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    getUserHierarchyTree()
      .then((data) => {
        if (alive) setTree(toTreeData(data?.users));
      })
      .catch((err) => {
        if (alive) setError(err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const term = search.trim().toLowerCase();
  const visible = useMemo(
    () => (term && tree ? filterTree(tree, term) : tree),
    [tree, term],
  );

  // While searching, expand everything so matches buried under a collapsed
  // manager are actually visible.
  const expandedProps = term ? { expandedKeys: collectKeys(visible ?? []) } : {};

  const content = (
    <div style={{ width: 280 }}>
      <Input.Search
        placeholder="Search users"
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      {loading && (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spin size="small" />
        </div>
      )}

      {error && <Empty description="Could not load users" />}

      {!loading && !error && (visible?.length ? (
        <div style={{ maxHeight: 280, overflow: 'auto' }}>
          <Tree
            checkable
            selectable={false}
            defaultExpandAll
            treeData={visible}
            checkedKeys={value}
            // antd omits half-checked ancestors from the first argument, so read
            // checkedNodes instead to get the fully-expanded id list — that is what
            // makes "check a manager => their whole team counts" true on the wire.
            onCheck={(_keys, info) => onChange(info.checkedNodes.map((n) => n.key))}
            {...expandedProps}
          />
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No users" />
      ))}

      {value.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
          <Text type="secondary">{value.length} selected</Text>
          <Button type="link" size="small" onClick={() => onChange([])}>
            Clear
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
    >
      <Badge count={value.length} size="small" offset={[-4, 4]}>
        <Button icon={<FilterOutlined />}>
          Filter by <DownOutlined style={{ fontSize: 10 }} />
        </Button>
      </Badge>
    </Popover>
  );
}
